import { useState, forwardRef } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import type { ElementType } from 'react';

import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { imageClasses } from './classes';
import type { ImageProps } from './types';

// ----------------------------------------------------------------------

const ImageWrapper = styled(Box)({
  overflow: 'hidden',
  position: 'relative',
  verticalAlign: 'bottom',
  display: 'inline-block',
  [`& .${imageClasses.wrapper}`]: {
    width: '100%',
    height: '100%',
    verticalAlign: 'bottom',
    backgroundSize: 'cover !important',
  },
});

const Overlay = styled('span')({
  top: 0,
  left: 0,
  zIndex: 1,
  width: '100%',
  height: '100%',
  position: 'absolute',
});

// ----------------------------------------------------------------------

interface EnhancedImageProps extends ImageProps {
  showNotAvailableText?: boolean;
}

export const Image = forwardRef<HTMLSpanElement, EnhancedImageProps>(
  (
    {
      ratio,
      disabledEffect = false,
      alt,
      src,
      delayTime,
      threshold,
      beforeLoad,
      delayMethod,
      placeholder,
      wrapperProps,
      scrollPosition,
      effect = 'blur',
      visibleByDefault,
      wrapperClassName,
      useIntersectionObserver,
      showNotAvailableText = false,
      slotProps,
      sx,
      ...other
    },
    ref
  ) => {
    const [isImageError, setIsImageError] = useState(false);

    const handleError = () => {
      setIsImageError(true);
    };

    if (isImageError) {
      return showNotAvailableText ? (
        <Typography variant="body1" color="text.disabled">
          N/A
        </Typography>
      ) : null;
    }

    const content = (
      <Box
        component={LazyLoadImage as ElementType}
        alt={alt}
        src={src}
        delayTime={delayTime}
        threshold={threshold}
        beforeLoad={beforeLoad}
        delayMethod={delayMethod}
        placeholder={placeholder}
        wrapperProps={wrapperProps}
        scrollPosition={scrollPosition}
        visibleByDefault={visibleByDefault}
        effect={visibleByDefault || disabledEffect ? undefined : effect}
        useIntersectionObserver={useIntersectionObserver}
        wrapperClassName={wrapperClassName || imageClasses.wrapper}
        placeholderSrc={
          visibleByDefault || disabledEffect ? `/assets/transparent.png` : `/assets/placeholder.svg`
        }
        sx={{
          width: 1,
          height: 1,
          objectFit: 'contain',
          verticalAlign: 'bottom',
          aspectRatio: ratio,
        }}
        onError={handleError}
      />
    );

    return (
      <ImageWrapper
        ref={ref}
        component="span"
        className={imageClasses.root}
        sx={{ ...(!!ratio && { width: 1 }), ...sx }}
        {...other}
      >
        {slotProps?.overlay && <Overlay className={imageClasses.overlay} sx={slotProps?.overlay} />}

        {content}
      </ImageWrapper>
    );
  }
);
